<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>{{ $t('referenceFamily.subtitle') }}</span>
      </div>
      <div class="ov-header__actions">
        <button type="button" class="ov-btn ov-btn--primary" @click="addNewReferenceDialog = true">
          <v-icon small>mdi-plus</v-icon>
          {{ $t('referenceFamily.newReferenceFamily') }}
        </button>
      </div>
    </div>

    <div class="ov-card">
      <div class="ov-card__head">
        <div class="ov-card__title">
          Familles
          <span class="ov-count">{{ referenceFamilleList.length }}</span>
        </div>
        <div class="ov-filters">
          <div class="ov-search">
            <v-icon small>mdi-magnify</v-icon>
            <input v-model="search" type="text" class="ov-input" :placeholder="$t('common.search')" />
          </div>
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="referenceFamilleList"
        :search="search"
        class="ov-table"
      >
        <template v-slot:[`item.designation`]="{ item }">
          <span class="rf-designation">{{ item.designation }}</span>
        </template>
        <template v-slot:[`item.description`]="{ item }">
          <span class="rf-muted">{{ item.description || '—' }}</span>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="ov-row-actions">
            <button
              type="button"
              class="ov-icon-btn"
              :title="$t('referenceFamily.edit')"
              @click="editReferenceFamille(item)"
            >
              <v-icon small>mdi-pencil-outline</v-icon>
            </button>
            <button
              type="button"
              class="ov-icon-btn ov-icon-btn--danger"
              :title="$t('referenceFamily.delete')"
              @click="supprimerReferenceFamille(item)"
            >
              <v-icon small>mdi-trash-can-outline</v-icon>
            </button>
          </div>
        </template>
      </v-data-table>
    </div>

    <!-- Panneau de création d'une famille -->
    <v-navigation-drawer
      v-model="addNewReferenceDialog"
      right
      fixed
      temporary
      width="420"
      class="ov-panel"
    >
      <div class="ov-panel__head">
        <div class="ov-card__title">
          {{ editingId ? $t('referenceFamily.edit') : $t('referenceFamily.addNewFamily') }}
        </div>
        <button type="button" class="ov-icon-btn" title="Fermer" @click="closeDialogReference()">
          <v-icon small>mdi-close</v-icon>
        </button>
      </div>

      <div class="ov-panel__body">
        <div class="ov-note ov-note--info">
          <v-icon small>mdi-information-outline</v-icon>
          <span>{{ $t('referenceFamily.requiredFields') }}</span>
        </div>

        <div class="ov-field">
          <label class="ov-label">{{ $t('referenceFamily.designation') }}<span class="ov-required">*</span></label>
          <input
            v-model.trim="$v.referenceFamilleObject.designation.$model"
            type="text"
            class="ov-input"
            :class="{ 'is-invalid': $v.referenceFamilleObject.designation.$error }"
          />
          <div v-if="$v.referenceFamilleObject.designation.$error" class="ov-error">
            {{ $t('referenceFamily.enterRequiredInfo') }}
          </div>
        </div>

        <div class="ov-field">
          <label class="ov-label">{{ $t('referenceFamily.description') }}</label>
          <textarea
            v-model.trim="referenceFamilleObject.description"
            class="ov-textarea"
            rows="4"
          ></textarea>
        </div>
      </div>

      <div class="ov-panel__foot">
        <button type="button" class="ov-btn ov-btn--secondary" @click="closeDialogReference()">
          {{ $t('referenceFamily.cancel') }}
        </button>
        <button type="button" class="ov-btn ov-btn--primary" @click="submitForm()">
          <v-icon small>mdi-check</v-icon>
          {{ $t('referenceFamily.save') }}
        </button>
      </div>
    </v-navigation-drawer>

    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="56" color="#159e72" />
    </v-overlay>
  </div>
</template>

<script>
import {
  API_CREATE_REFERENCE_FAMILLE,
  HEADERS,
} from "@/components/globalConfig/globalConstConfig";
import {
  showErrorMessage,
  showSuccessMessage,
} from "@/modules/messages/messageProcess";
import axios from "axios";
import { required } from "vuelidate/lib/validators";
export default {
  name: "AddOrSelectReferenceFamille",
  data() {
    return {
      overlay: false,
      errorMsg: null,
      successMsg: null,
      addNewReferenceDialog: false,

      headers: [
        { text: this.$t('referenceFamily.reference'), value: "designation" },
        { text: this.$t('referenceFamily.description'), value: "description" },
        { text: this.$t('referenceFamily.actions'), value: "actions", sortable: false },
      ],
      search: "",

      referenceFamilleDataToSend: {
        datas: [],
      },

      referenceFamilleObject: {
        designation: null,
        description: null,
      },

      // id de la famille en cours de modification (null = création)
      editingId: null,

      referenceFamilleList: [],
    };
  },

  validations: {
    referenceFamilleObject: {
      designation: {
        required,
      },
    },
  },

  methods: {
    reinitializeReferenceData() {
      this.referenceFamilleObject.designation = null;
      this.referenceFamilleObject.description = null;
      this.editingId = null;
    },

    editReferenceFamille(item) {
      this.referenceFamilleObject.designation = item.designation;
      this.referenceFamilleObject.description = item.description;
      this.editingId = item.id;
      this.addNewReferenceDialog = true;
    },

    // SUPPRIMER UNE FAMILLE DE RÉFÉRENCES (APRÈS CONFIRMATION)
    async supprimerReferenceFamille(item) {
      const confirmation = await this.$swal.fire({
        title: this.$t('referenceFamily.delete'),
        text: `« ${item.designation} » sera supprimée définitivement.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        confirmButtonText: "Supprimer",
        cancelButtonText: "Annuler",
      });
      if (!confirmation.isConfirmed) return;

      this.overlay = true;
      await axios
        .delete(`${API_CREATE_REFERENCE_FAMILLE}/${item.id}`, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.data.status.code == 800) {
            this.successMsg = response.data.status.message;
            showSuccessMessage();
            this.apiGetReferenceFamily();
          } else {
            this.errorMsg = response.data.status.message;
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {
          this.overlay = false;
        });
    },

    // Close dialog allows creating family reference
    closeDialogReference() {
      this.addNewReferenceDialog = false;
      this.$v.$reset();
      this.reinitializeReferenceData();
    },

    // Submit form to create or update a reference family
    async submitForm() {
      this.$v.$touch();
      if (this.$v.referenceFamilleObject.$invalid) {
        this.errorMsg = this.$t('referenceFamily.enterRequiredInfo');
        showErrorMessage();
        return;
      }
      const saved = await this.apiCreateReferenceFamily();
      if (saved) {
        this.closeDialogReference();
      }
    },

    // API CREATE (POST) OR UPDATE (PUT, AVEC L'ID) A REFERENCE FAMILY
    async apiCreateReferenceFamily() {
      this.overlay = true;
      let saved = false;
      // Copie : la remise à zéro du formulaire ne doit pas vider les données envoyées
      const famille = Object.assign({}, this.referenceFamilleObject);
      if (this.editingId) {
        famille.id = this.editingId;
      }
      this.referenceFamilleDataToSend.datas = [famille];
      const request = this.editingId ? axios.put : axios.post;
      await request(API_CREATE_REFERENCE_FAMILLE, this.referenceFamilleDataToSend, {
        headers: HEADERS(this.$store.state.userAuthentified.token),
      })
        .then((response) => {
          if (response.data.status.code == 800) {
            saved = true;
            this.successMsg = response.data.status.message;
            showSuccessMessage();
            this.apiGetReferenceFamily();
          } else {
            this.errorMsg = response.data.status.message;
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {
          this.referenceFamilleDataToSend.datas = [];
          this.overlay = false;
        });
      return saved;
    },

    // API CREATE REFERENCE FAMILY
    async apiGetReferenceFamily() {
      await axios
        .get(API_CREATE_REFERENCE_FAMILLE)
        .then((response) => {
          if (response.data.status.code == 800) {
            this.referenceFamilleList = response.data.items;
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {});
    },
  },

  mounted() {
    this.apiGetReferenceFamily();
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/admin/ui";

.rf-designation {
  font-weight: 700;
  color: $ov-navy;
}

.rf-muted {
  color: $ov-muted;
}
</style>
